import React from 'react';
import useGameStore from '../../store/gameStore';
import { formatMoney } from '../../utils/format';
import { IS_RATES, TAX_OPTIMIZATIONS } from '../../data/taxes';
import { projectAnnualProfit } from '../../utils/projectAnnualProfit';
import { Landmark, CheckCircle2, Clock } from 'lucide-react';
import ProfitHistoryChart from './ProfitHistoryChart';
import FiscalSimulator from './FiscalSimulator';
import CapexSimulator from './CapexSimulator';
import AuditFiscalPanel from './AuditFiscalPanel';

/**
 * Fiscalité — onglet Impôt sur les Sociétés. Affiche les taux, estimations,
 * le détail de calcul, le graphique d'évolution, le simulateur DAF, l'audit
 * et les acomptes trimestriels. Extracted from Fiscalite.jsx on 28/05/2026.
 */
export default function IsTab() {
  const {
    annualProfit, isInstallmentsPaid, isPendingInstallment, payIsInstallment,
    month, treasury, activeOptimizations,
  } = useGameStore();

  // 12/02/2026 — Lissage début d'année via `projectAnnualProfit`
  const { annualProfit: estimatedAnnualProfit, annualIS: estimatedIS, damped } =
    projectAnnualProfit({ annualProfit, month });

  return (
    <div className="space-y-4">
      <div className="bg-ink-800 rounded-lg border border-ink-600 p-4">
        <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
          <Landmark className="w-4 h-4" /> Impôt sur les Sociétés (IS)
        </h3>

        {/* IS rates */}
        <div className="bg-ink-700/30 rounded-lg p-3 mb-4">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <p className="text-slate-500">Taux réduit PME</p>
              <p className="font-bold text-foreground">{(IS_RATES.reduced.rate * 100)}% <span className="font-normal text-slate-500">(jusqu&apos;à {formatMoney(IS_RATES.reduced.maxProfit)})</span></p>
            </div>
            <div>
              <p className="text-slate-500">Taux normal</p>
              <p className="font-bold text-foreground">{(IS_RATES.standard.rate * 100)}%</p>
            </div>
          </div>
        </div>

        {/* Current year estimates */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div className="bg-accent/10 rounded-lg p-3 text-center">
            <p className="text-[10px] text-slate-500">Bénéfice cumulé</p>
            <p className={`text-lg font-bold ${annualProfit >= 0 ? 'text-foreground' : 'text-danger'}`}>
              {formatMoney(Math.round(annualProfit))}
            </p>
            <p className="text-[10px] text-slate-500 mt-0.5">CA − charges depuis le 1er jan</p>
          </div>
          <div className="bg-warn/10 rounded-lg p-3 text-center">
            <p className="text-[10px] text-slate-500">IS estimé annuel</p>
            <p className="text-lg font-bold text-warn">{formatMoney(estimatedIS)}</p>
            <p className="text-[10px] text-slate-500 mt-0.5">Sur bénéfice annualisé</p>
          </div>
        </div>

        {/* IS calculation transparency — explicit chain from bénéfice to acompte. */}
        <div
          className="bg-ink-800/60 rounded-lg p-3 border border-ink-600 mb-4 text-[11px] text-slate-400 leading-relaxed"
          data-testid="is-calc-breakdown"
        >
          <p className="text-xs font-semibold text-slate-300 mb-1.5">📐 Calcul de l&apos;IS — détail</p>
          <div className="flex justify-between">
            <span>Bénéfice cumulé (mois {month}/12)</span>
            <span className="text-slate-200 font-mono">{formatMoney(Math.round(annualProfit))}</span>
          </div>
          <div className="flex justify-between">
            <span>× (12 ÷ {damped ? 3 : month}) → Bénéfice annualisé{damped ? ' (lissé)' : ''}</span>
            <span className="text-slate-200 font-mono">{formatMoney(Math.round(estimatedAnnualProfit))}</span>
          </div>
          <div className="flex justify-between border-t border-ink-700 mt-1 pt-1">
            <span>Barème IS (15 % &lt; 42 500 € / 25 % au-delà)</span>
            <span className="text-warn font-mono">{formatMoney(estimatedIS)}</span>
          </div>
          <div className="flex justify-between">
            <span>÷ 4 → Acompte trimestriel</span>
            <span className="text-warn font-mono">{formatMoney(Math.round(estimatedIS / 4))}</span>
          </div>
          {damped && (
            <p
              className="text-[10px] text-emerald-300 mt-1.5 italic"
              data-testid="is-calc-damped-hint"
            >
              🛡️ Estimation lissée : plancher × 4 au lieu de × {annualProfit > 0 && month === 1 ? '12' : '6'} (mois {month} &lt; 3, échantillon trop court). Le premier acompte T1 (mars) utilise la formule standard.
            </p>
          )}
          {annualProfit <= 0 && (
            <p className="text-[10px] text-emerald-300 mt-1.5 italic">
              Bénéfice ≤ 0 → aucun IS dû cette année. Profitez-en pour investir dans la croissance.
            </p>
          )}
        </div>

        {/* Monthly evolution chart — bénéfice cumulé + IS estimé. */}
        <div className="mb-4">
          <ProfitHistoryChart />
        </div>

        {/* Fiscal what-if simulator (DAF unlock). */}
        <div className="mb-4">
          <FiscalSimulator />
        </div>

        {/* CAPEX prospective simulator (DAF unlock) — Phase B 28/05/2026 v2.
            Compares cumulative IS over 12-36 months with vs. without a
            given depot/office/software/website investment. */}
        <div className="mb-4">
          <CapexSimulator />
        </div>

        {/* Audit fiscal — gated to Sophie Lambert (Comptable). */}
        <div className="mb-4">
          <AuditFiscalPanel />
        </div>

        {/* Quarterly installments status */}
        <p className="text-xs font-semibold text-slate-400 mb-2">Acomptes trimestriels</p>
        <div className="grid grid-cols-4 gap-2 mb-4">
          {[1, 2, 3, 4].map(q => {
            const paid = isInstallmentsPaid.find(i => i.quarter === q);
            const isCurrent = Math.ceil(month / 3) === q;
            return (
              <div key={q} className={`rounded-lg p-2.5 text-center border ${
                paid ? 'border-money/40 bg-money/10' :
                isCurrent && isPendingInstallment ? 'border-warn/60 bg-warn/10' :
                'border-ink-600'
              }`}>
                <p className="text-xs font-bold text-slate-300">T{q}</p>
                {paid ? (
                  <div>
                    <CheckCircle2 className="w-4 h-4 text-money mx-auto my-1" />
                    <p className="text-[10px] text-money font-medium">{formatMoney(paid.amount)}</p>
                  </div>
                ) : (
                  <div>
                    <Clock className={`w-4 h-4 mx-auto my-1 ${isCurrent ? 'text-warn' : 'text-slate-600'}`} />
                    <p className="text-[10px] text-slate-500">{isCurrent ? 'En attente' : 'À venir'}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {isPendingInstallment && (
          <button
            onClick={payIsInstallment}
            disabled={treasury < Math.round(estimatedIS / 4)}
            className="w-full bg-accent text-white py-2.5 rounded-lg text-sm font-bold hover:bg-accent-hover disabled:opacity-40 transition-colors"
            data-testid="pay-is-installment-btn"
          >
            Payer l&apos;acompte T{Math.ceil(month / 3)} ({formatMoney(Math.round(estimatedIS / 4))})
          </button>
        )}

        {/* Active optimizations */}
        {Object.keys(activeOptimizations).length > 0 && (
          <div className="mt-3 border-t border-ink-600 pt-3">
            <p className="text-xs text-slate-500 mb-1">Réductions actives :</p>
            {Object.keys(activeOptimizations).map(optId => {
              const opt = TAX_OPTIMIZATIONS[optId];
              return opt ? (
                <p key={optId} className="text-xs text-money flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> {opt.name}
                </p>
              ) : null;
            })}
          </div>
        )}
      </div>
    </div>
  );
}
